import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token');

    const [verifying, setVerifying] = useState(true);
    const [valid, setValid] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        if (!token) {
            setVerifying(false);
            return;
        }
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/auth/reset-password/verify`, { params: { token } });
                setValid(true);
                setEmail(res.data?.email || '');
            } catch (e) {
                setValid(false);
            } finally {
                setVerifying(false);
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error('Password minimal 8 karakter');
        if (password !== confirm) return toast.error('Password konfirmasi tidak cocok');
        try {
            setSubmitting(true);
            await axios.post(`${API_BASE}/api/auth/reset-password`, { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Gagal reset password');
        } finally {
            setSubmitting(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-background">
                <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-brand-background p-4">
            <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-brand-border p-8" data-testid="reset-password-page">
                {!valid ? (
                    <div className="text-center py-4" data-testid="reset-invalid">
                        <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-brand-text-primary mb-2">Link Tidak Valid</h2>
                        <p className="text-sm text-brand-text-secondary mb-6">
                            Link reset password tidak valid atau sudah kadaluarsa. Silakan minta link baru.
                        </p>
                        <Link to="/forgot-password">
                            <Button className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white" data-testid="reset-request-new">
                                Minta Link Baru
                            </Button>
                        </Link>
                    </div>
                ) : done ? (
                    <div className="text-center py-4" data-testid="reset-done">
                        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-brand-text-primary mb-2">Password Berhasil Direset</h2>
                        <p className="text-sm text-brand-text-secondary mb-4">Anda akan dialihkan ke halaman login...</p>
                    </div>
                ) : (
                    <>
                        <h1 className="text-2xl font-bold text-brand-text-primary mb-2 flex items-center gap-2">
                            <Lock className="w-6 h-6 text-brand-primary" /> Set Password Baru
                        </h1>
                        <p className="text-sm text-brand-text-secondary mb-6">
                            untuk <strong>{email}</strong>
                        </p>
                        <form onSubmit={submit} className="space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="new-password" className="text-brand-text-primary font-medium">Password Baru</Label>
                                <div className="relative">
                                    <Input
                                        id="new-password"
                                        type={show ? 'text' : 'password'}
                                        placeholder="Minimal 8 karakter"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        className="bg-brand-background border-brand-border pr-10"
                                        data-testid="reset-password-input"
                                    />
                                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password" className="text-brand-text-primary font-medium">Konfirmasi Password</Label>
                                <div className="relative">
                                <Input
                                    id="confirm-password"
                                    type={showConfirm ? 'text' : 'password'}
                                    placeholder="Ulangi password baru"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    minLength={8}
                                    className="bg-brand-background border-brand-border"
                                    data-testid="reset-confirm-input"
                                    />
                                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button type="submit" disabled={submitting} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white h-11" data-testid="reset-submit-btn">
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Reset Password
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
